import playgroundApi from "../playgroundApi";
import {getComputerId, getComputerType} from "../device/deviceInfo";
import {deleteSignInData, getSignInDataFromLocalStorage, saveSignInDataToLocalStorage} from "./sign-in-data";
import {currentSignInDataVersion} from "../../config/config";
import productMonitoring from "../log/productMonitoring";
import {myProfile} from "./my-profile";
import nativeBridge from "../nativeBridge";
import {inAppAnalyticsService, LoginAnalyticsMethod} from "../inAppAnalyticsService";
import IAuthenticationResponse from "../../client/models/authentication/IAuthenticationResponse";
import {SocialSignInProvider} from "../../client/models/authentication/SocialSignInProvider";

interface ISignupDetails {
  email: string,
  password: string
}

class UserAuthentication {

  public async signIn(username: string, password: string){
    const startSessionResponse = await playgroundApi.authentication(
      username,
      password,
      getComputerId(),
      getComputerType()
    );
    await this.hydrateSessionAndNotify(startSessionResponse, "password");
  }

  /**
   * Shared post-login path for password and social accept (hydrate → persist →
   * signedIn → loadProfile → analytics). PlaygroundAPI already hydrated session
   * fields on accept/authentication responses.
   */
  public async completeSocialSignIn(
    startSessionResponse: IAuthenticationResponse,
    method: SocialSignInProvider,
  ) {
    await this.hydrateSessionAndNotify(startSessionResponse, method);
  }

  private async hydrateSessionAndNotify(
    startSessionResponse: IAuthenticationResponse,
    method: LoginAnalyticsMethod,
  ) {
    const {
      session: {
        token: sessionToken,
        id: sessionId, expiration
      },
      user: {
        id: userId,
        soundsliceUserIdentifier,
      },
    } = startSessionResponse;
    const tokenExpiryDate = new Date(expiration);
    const tokenExpiryTime = tokenExpiryDate.getTime();

    // Ensure PlaygroundAPI singleton matches response (password path also sets these in authentication()).
    playgroundApi.sessionId = sessionId.toString();
    playgroundApi.sessionToken = sessionToken;
    playgroundApi.userId = userId;

    saveSignInDataToLocalStorage({
      sessionId: sessionId.toString(),
      sessionToken,
      userId,
      tokenExpiryTime,
      version: currentSignInDataVersion,
      soundsliceUserIdentifier,
    });

    nativeBridge.sendMessageToNative({
      type: "user.signedIn",
      userId: userId
    });

    await myProfile.loadProfile();

    inAppAnalyticsService.onLogin_success(method);
  }

  public async signUp({
    email,
    password
  }: ISignupDetails){
    //build request object
    const computerId = getComputerId();
    const computerType = getComputerType();

    // send request
    const result = await playgroundApi.createAccount({
      username: email,
      email,
      password,
      computerId,
      computerType
    });
    const {session: {id: sessionId, token: sessionToken, expiration}, user: {id: userId, soundsliceUserIdentifier,}} = result;
    const tokenExpiryDate = new Date(expiration);
    const tokenExpiryTime = tokenExpiryDate.getTime();
    saveSignInDataToLocalStorage({
      sessionId: sessionId.toString(),
      sessionToken,
      userId,
      tokenExpiryTime,
      version: currentSignInDataVersion,
      soundsliceUserIdentifier,
    });

    // Notify native layer that user signed up
    nativeBridge.sendMessageToNative({
      type: "user.signedUp",
      userId: userId
    });

    void myProfile.loadProfile();
  }

  public signOut(){
    deleteSignInData();
    playgroundApi.deleteUserSession();
    // reset product monitoring session
    productMonitoring.resetSession();

    // Notify native layer that user signed out
    const signInData = getSignInDataFromLocalStorage();
    nativeBridge.sendMessageToNative({
      type: "user.signedOut",
      userId: signInData?.userId ?? 0
    });

    // trigger my profile to trigger reload
    myProfile.triggerReload();
  }
}

export const userAuthentication = new UserAuthentication();
